import { useTranslation } from "react-i18next";
import logo from "../assets/logo.svg";

type AgeGatePromptProps = {
  onConfirm: () => void;
  onDecline: () => void;
  mode?: "gate" | "blocked";
};

export default function AgeGatePrompt({
  onConfirm,
  onDecline,
  mode = "gate",
}: AgeGatePromptProps) {
  const { t } = useTranslation();

  if (mode === "blocked") {
    return (
      <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-black/90 px-6 py-10">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#141414] p-8 text-center shadow-xl">
          <div className="text-xl font-semibold text-white">KinkyFantasy</div>
          <p className="mt-4 text-sm leading-relaxed text-gray-400">
            {t("ageGate.blockedNotice", {
              defaultValue:
                "This website is only intended for users over the age of 18.",
            })}
          </p>
        </div>
      </div>
    );
  }

  const rules: Array<{ key: string; emoji: string; defaultValue: string }> = [
    {
      key: "age",
      emoji: "🔞",
      defaultValue: "You must be 18+ to use this website.",
    },
    {
      key: "about",
      emoji: "🎭",
      defaultValue:
        "Create and chat with personalized AI companions, customized to your fantasies.",
    },
    {
      key: "responsibility",
      emoji: "🙋",
      defaultValue: "You are solely responsible for how you use this app.",
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center overflow-hidden bg-black px-6 py-10">
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-brand/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />

      <div className="relative w-full max-w-sm rounded-[32px] border border-white/10 bg-[#1a1a1a]/90 p-8 text-center shadow-2xl backdrop-blur">
        <p className="text-sm text-gray-400">
          {t("ageGate.welcomeLabel", { defaultValue: "Welcome" })}
        </p>
        <div className="mt-3 flex items-center justify-center gap-2">
          <img src={logo} alt="" className="h-8 w-8 rounded-lg" />
          <span className="text-2xl font-bold text-white">KinkyFantasy</span>
        </div>

        <div className="mt-7 flex flex-col gap-3">
          {rules.map((rule) => (
            <div
              key={rule.key}
              className="flex items-center gap-3 rounded-3xl bg-white/5 px-5 py-4 text-left"
            >
              <span className="text-xl">{rule.emoji}</span>
              <span className="text-sm leading-snug text-gray-200">
                {t(`ageGate.rules.${rule.key}`, {
                  defaultValue: rule.defaultValue,
                })}
              </span>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs leading-relaxed text-gray-500">
          {t("ageGate.agreementNotice", {
            defaultValue: "By continuing to use, you agree to the rules above.",
          })}
        </p>

        <button
          type="button"
          onClick={onConfirm}
          className="mt-5 w-full rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-black transition hover:bg-gray-200"
        >
          {t("ageGate.confirmButton", { defaultValue: "Continue" })}
        </button>
        <button
          type="button"
          onClick={onDecline}
          className="mt-4 text-xs text-gray-500 underline-offset-2 transition hover:text-gray-300 hover:underline"
        >
          {t("ageGate.declineButton", { defaultValue: "I'm under 18" })}
        </button>
      </div>
    </div>
  );
}
